import type { CurrencyFormat, GameSnapshot, JurisdictionProfile, MathConfig, ResolvedBrand, ResolvedTheme, Strings } from '../types';
import type { AudioManager } from '../audio/audio';
import type { Game } from '../engine/game';
import { brandCssVars } from '../config/brand';
import { formatDuration, formatMoney, t } from '../util/format';
import { DebugPanel } from './debug';
import { createModal, el, type Modal } from './modal';
import { Paytable } from './paytable';
import { Settings } from './settings';
import { injectStyles, paletteToCssVars } from './styles';

export interface HudOptions {
  container: HTMLElement;
  game: Game;
  math: MathConfig;
  jurisdiction: JurisdictionProfile;
  theme: ResolvedTheme;
  /** Brand (interface chrome) — null for the plain theme look. */
  brand: ResolvedBrand | null;
  strings: Strings;
  currency: CurrencyFormat;
  audio: AudioManager;
  debug: boolean;
}

const ICONS = {
  info: '<svg viewBox="0 0 24 24"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>',
  settings:
    '<svg viewBox="0 0 24 24"><path d="M19.4 13a7.6 7.6 0 0 0 0-2l2.1-1.6-2-3.5-2.5 1a7.7 7.7 0 0 0-1.7-1L15 3h-4l-.4 2.7a7.7 7.7 0 0 0-1.7 1l-2.5-1-2 3.5L6.6 11a7.6 7.6 0 0 0 0 2l-2.1 1.6 2 3.5 2.5-1a7.7 7.7 0 0 0 1.7 1L11 21h4l.4-2.7a7.7 7.7 0 0 0 1.7-1l2.5 1 2-3.5L19.4 13zM13 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z"/></svg>',
  sound: '<svg viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM14 3.2v2.1a7 7 0 0 1 0 13.4v2.1a9 9 0 0 0 0-17.6z"/></svg>',
  mute: '<svg viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.6 3 2.7-2.7-1.4-1.4L15.2 10.6l-2.7-2.7-1.4 1.4 2.7 2.7-2.7 2.7 1.4 1.4 2.7-2.7 2.7 2.7 1.4-1.4L16.6 12z"/></svg>',
} as const;

type IconName = keyof typeof ICONS;
const SPIN_ART_STATES = ['default', 'pressed', 'disabled'] as const;
type SpinArtState = (typeof SPIN_ART_STATES)[number];

/** Decode the themed spin-button frames before the first round so state switches never trigger a load. */
export async function preloadSpinArt(theme: ResolvedTheme, timeoutMs = 4000): Promise<void> {
  const art = theme.assets.spinButton;
  if (!art || typeof Image === 'undefined') return;
  const one = (src: string) =>
    new Promise<void>((resolve) => {
      const img = new Image();
      const done = () => resolve();
      img.onload = () => (img.decode ? img.decode().then(done, done) : done());
      img.onerror = done;
      img.src = src;
      setTimeout(done, timeoutMs);
    });
  await Promise.all([art.default, art.pressed, art.disabled].map(one));
}

/** DOM HUD: readouts, bet stepper, spin button, overlays. Styled purely by --slot-* variables; brand-aware. */
export class Hud {
  private readonly o: HudOptions;
  private readonly root: HTMLElement;
  private readonly bar: HTMLDivElement;
  private readonly balanceEl: HTMLSpanElement;
  private readonly balanceReadout: HTMLDivElement;
  private readonly betEl: HTMLSpanElement;
  private readonly winEl: HTMLSpanElement;
  private readonly winReadout: HTMLDivElement;
  private readonly freeReadout: HTMLDivElement;
  private readonly freeEl: HTMLSpanElement;
  private readonly sessionReadout: HTMLDivElement;
  private readonly sessionEl: HTMLSpanElement;
  private readonly netReadout: HTMLDivElement;
  private readonly netEl: HTMLSpanElement;
  private readonly betMinus: HTMLButtonElement;
  private readonly betPlus: HTMLButtonElement;
  private readonly betMax: HTMLButtonElement;
  private readonly spinBtn: HTMLButtonElement;
  private readonly spinLabel: HTMLSpanElement;
  private readonly spinArt: Record<SpinArtState, HTMLImageElement>;
  private spinOrbit!: HTMLSpanElement;
  private readonly infoBtn: HTMLButtonElement;
  private readonly gearBtn: HTMLButtonElement;
  private readonly soundBtn: HTMLButtonElement;
  private readonly iconsGroup: HTMLDivElement;
  private readonly readoutsRow: HTMLDivElement;
  private readonly controlsRow: HTMLDivElement;
  private readonly toast: HTMLDivElement;
  private readonly loading: HTMLDivElement;
  private readonly loadingLogo: HTMLImageElement;
  private readonly loadingFill: HTMLDivElement;
  private readonly loadingLabel: HTMLDivElement;
  private readonly paytable: Paytable;
  private readonly settings: Settings;
  private debugPanel: DebugPanel | null = null;
  private pausedModal: Modal | null = null;
  private realityModal: Modal | null = null;
  private strings: Strings;
  private theme: ResolvedTheme;
  private brand: ResolvedBrand | null;
  private brandLogo: HTMLElement | null = null;
  private brandStyle: HTMLStyleElement | null = null;
  private snapshot: GameSnapshot;
  private readonly unsubscribe: Array<() => void> = [];
  private timers: ReturnType<typeof setInterval>[] = [];
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private lastRealityCheck = Date.now();
  private resizeObserver: ResizeObserver | null = null;
  private readonly onKey = (e: KeyboardEvent) => this.handleKey(e);
  private readonly onKeyUp = (e: KeyboardEvent) => {
    if (e.code === 'Space' || e.code === 'Enter') this.spinBtn.classList.remove('is-pressed');
  };
  private readonly onBlur = () => this.spinBtn.classList.remove('is-pressed');

  constructor(o: HudOptions) {
    this.o = o;
    this.root = o.container;
    this.strings = o.strings;
    this.theme = o.theme;
    this.brand = o.brand;
    injectStyles(this.root);
    this.applyTheme(o.theme);
    this.snapshot = o.game.snapshot;

    // ---- bar
    this.bar = el('div', 'slot-hud');
    const readouts = el('div', 'slot-hud__readouts');
    const readout = (mod: string, label: string) => {
      const r = el('div', `slot-readout slot-readout--${mod}`);
      const l = el('span', 'slot-readout__label', label);
      const v = el('span', 'slot-readout__value', '');
      r.append(l, v);
      readouts.appendChild(r);
      return { r, l, v };
    };
    const bal = readout('balance', t(this.strings, 'balance'));
    this.balanceEl = bal.v;
    this.balanceReadout = bal.r;
    const bet = readout('bet', t(this.strings, 'bet'));
    const win = readout('win', t(this.strings, 'win'));
    this.winEl = win.v;
    this.winReadout = win.r;
    const free = readout('free', t(this.strings, 'freeSpinsLeft'));
    this.freeReadout = free.r;
    this.freeEl = free.v;
    this.freeReadout.hidden = true;
    const session = readout('session', t(this.strings, 'sessionTime'));
    this.sessionReadout = session.r;
    this.sessionEl = session.v;
    this.sessionReadout.hidden = !o.jurisdiction.showSessionClock;
    const net = readout('net', t(this.strings, 'netPosition'));
    this.netReadout = net.r;
    this.netEl = net.v;
    this.netReadout.hidden = !o.jurisdiction.showNetPosition;

    const controls = el('div', 'slot-hud__controls');
    const left = el('div', 'slot-hud__group slot-hud__icons');
    this.infoBtn = this.iconButton('info', t(this.strings, 'paytable'), () => this.openPaytable());
    this.gearBtn = this.iconButton('settings', t(this.strings, 'settings'), () => this.openSettings());
    this.soundBtn = this.iconButton(o.audio.enabled ? 'sound' : 'mute', t(this.strings, 'sound'), () => this.toggleSound());
    left.append(this.infoBtn, this.gearBtn, this.soundBtn);

    const betBox = el('div', 'slot-bet');
    this.betMinus = el('button', 'slot-btn', '−');
    this.betMinus.type = 'button';
    this.betMinus.addEventListener('click', () => this.stepBet(-1));
    const betValue = el('div', 'slot-bet__value');
    this.betMax = el('button', 'slot-btn slot-bet__max', t(this.strings, 'maxBet'));
    this.betMax.type = 'button';
    this.betMax.addEventListener('click', () => this.maxBet());
    betValue.append(el('span', 'slot-bet__label', t(this.strings, 'bet')), (this.betEl = el('span', 'slot-bet__amount', '')), this.betMax);
    this.betPlus = el('button', 'slot-btn', '+');
    this.betPlus.type = 'button';
    this.betPlus.addEventListener('click', () => this.stepBet(1));
    betBox.append(this.betMinus, betValue, this.betPlus);
    bet.r.hidden = true; // bet lives in the stepper

    const right = el('div', 'slot-hud__group');
    this.spinBtn = el('button', 'slot-spin');
    this.spinBtn.type = 'button';
    // Illustrated button: three stacked PNG states (default / pressed / disabled) under a DOM label.
    this.spinArt = {} as Record<SpinArtState, HTMLImageElement>;
    for (const state of SPIN_ART_STATES) {
      const img = el('img', `slot-spin__art slot-spin__art--${state}`);
      img.alt = '';
      img.draggable = false;
      img.width = 768;
      img.height = 256;
      img.setAttribute('aria-hidden', 'true');
      this.spinArt[state] = img;
      this.spinBtn.appendChild(img);
    }
    this.spinLabel = el('span', 'slot-spin__label', t(this.strings, 'spin'));
    this.spinBtn.appendChild(this.spinLabel);
    this.spinOrbit = el('span', 'slot-spin__orbit');
    this.spinOrbit.hidden = true;
    this.spinBtn.appendChild(this.spinOrbit);
    this.spinBtn.addEventListener('pointerdown', () => this.spinBtn.classList.add('is-pressed'));
    for (const ev of ['pointerup', 'pointerleave', 'pointercancel'] as const) this.spinBtn.addEventListener(ev, () => this.spinBtn.classList.remove('is-pressed'));
    this.spinBtn.addEventListener('click', () => {
      o.audio.play('click');
      o.game.press();
    });
    right.appendChild(this.spinBtn);
    controls.append(left, betBox, right);
    this.bar.append(readouts, controls);
    this.iconsGroup = left;
    this.readoutsRow = readouts;
    this.controlsRow = controls;
    this.root.appendChild(this.bar);

    // ---- toast + loading
    this.toast = el('div', 'slot-toast');
    this.root.appendChild(this.toast);
    this.loading = el('div', 'slot-loading');
    this.loadingLogo = el('img', 'slot-loading__logo');
    const title = el('h1', 'slot-loading__title', o.theme.name);
    this.loadingLabel = el('div', 'slot-loading__label', t(this.strings, 'loading'));
    const barEl = el('div', 'slot-loading__bar');
    this.loadingFill = el('div', 'slot-loading__fill');
    barEl.appendChild(this.loadingFill);
    this.loading.append(this.loadingLogo, title, this.loadingLabel, barEl);
    this.root.appendChild(this.loading);

    this.paytable = new Paytable({ root: this.root, math: o.math, theme: o.theme, strings: this.strings, currency: o.currency });
    this.settings = new Settings({ root: this.root, strings: this.strings, jurisdiction: o.jurisdiction, audio: o.audio, game: o.game });
    if (o.debug) this.debugPanel = new DebugPanel({ root: this.root, game: o.game, math: o.math, jurisdiction: o.jurisdiction, strings: this.strings });

    // ---- wiring
    const g = o.game;
    this.unsubscribe.push(
      g.on('snapshot', (s) => this.onSnapshot(s)),
      g.on('balance', ({ balance }) => (this.balanceEl.textContent = formatMoney(balance, o.currency))),
      g.on('bet', ({ bet: b }) => (this.betEl.textContent = formatMoney(b, o.currency))),
      g.on('spinStart', () => this.setWin(0)),
      g.on('win', ({ book }) => this.setWin(book.totalWin)),
      g.on('freeSpinsUpdate', (fs) => {
        this.freeEl.textContent = `${fs.remaining} / ${fs.total}`;
        this.freeReadout.hidden = false;
      }),
      g.on('freeSpinsEnd', () => (this.freeReadout.hidden = true)),
      g.on('error', ({ code, message }) => this.showToast(code === 'INSUFFICIENT_FUNDS' ? t(this.strings, 'insufficientBalance') : message)),
      g.on('state', ({ state, previous }) => {
        if (state === 'paused') this.showPaused();
        else if (previous === 'paused') this.hidePaused();
      }),
    );
    this.balanceEl.textContent = formatMoney(this.snapshot.balance, o.currency);
    this.betEl.textContent = formatMoney(this.snapshot.bet, o.currency);
    this.setWin(this.snapshot.lastWin);
    this.onSnapshot(this.snapshot);

    this.applyBrand(o.brand, o.theme);

    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    this.resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.layout()) : null;
    this.resizeObserver?.observe(this.root);
    this.layout();

    this.timers.push(setInterval(() => this.tickClock(), 1000));
    if (o.jurisdiction.realityCheckMinutes) this.timers.push(setInterval(() => this.checkReality(), 15_000));
  }

  // ------------------------------------------------------------------ building blocks

  private iconButton(icon: IconName, label: string, onClick: () => void): HTMLButtonElement {
    const b = el('button', 'slot-btn slot-btn--icon');
    b.type = 'button';
    b.dataset.icon = icon;
    b.title = label;
    b.setAttribute('aria-label', label);
    this.renderIcon(b, icon);
    b.addEventListener('click', () => {
      this.o.audio.play('click');
      onClick();
    });
    return b;
  }

  /** Built-in inline SVG, or the brand's image for this icon. */
  private renderIcon(button: HTMLButtonElement, icon: IconName): void {
    button.dataset.icon = icon;
    const custom = this.brand?.icons[icon];
    if (custom) {
      button.innerHTML = '';
      const img = el('img');
      img.src = custom;
      img.alt = '';
      button.appendChild(img);
    } else button.innerHTML = ICONS[icon];
  }

  private applyTheme(theme: ResolvedTheme): void {
    this.theme = theme;
    const vars = paletteToCssVars(theme.palette, theme.fonts);
    for (const [k, v] of Object.entries(vars)) this.root.style.setProperty(k, v);
  }

  /** Apply the brand chrome: CSS variables, layout variant, buttons, visibility, logo, icons, custom CSS. */
  private applyBrand(brand: ResolvedBrand | null, theme: ResolvedTheme): void {
    this.brand = brand;
    for (const [k, v] of Object.entries(brandCssVars(brand, theme))) this.root.style.setProperty(k, v);
    const hud = brand?.hud;
    const buttons = brand?.buttons;
    this.root.classList.toggle('slot-root--btn-outline', buttons?.style === 'outline');
    this.root.classList.toggle('slot-root--hud-top', hud?.position === 'top');
    this.bar.classList.toggle('slot-hud--top', hud?.position === 'top');
    this.bar.classList.toggle('slot-hud--floating', hud?.style === 'floating');
    this.bar.classList.toggle('slot-hud--minimal', hud?.style === 'minimal');
    this.infoBtn.hidden = hud ? !hud.showPaytable : false;
    this.gearBtn.hidden = hud ? !hud.showSettings : false;
    this.soundBtn.hidden = hud ? !hud.showSound : false;
    this.iconsGroup.hidden = this.infoBtn.hidden && this.gearBtn.hidden && this.soundBtn.hidden;
    this.winReadout.hidden = hud ? !hud.showWin : false;
    this.balanceReadout.hidden = hud ? !hud.showBalance : false;
    this.betMax.hidden = hud ? !hud.showMaxBet : false;
    for (const b of [this.infoBtn, this.gearBtn]) this.renderIcon(b, b.dataset.icon as IconName);
    this.renderIcon(this.soundBtn, this.o.audio.enabled ? 'sound' : 'mute');
    this.setSpinLabel(t(this.strings, 'spin'));
    this.betMax.textContent = t(this.strings, 'maxBet');
    const orbit = !!brand?.buttons.spinOrbit;
    this.spinOrbit.hidden = !orbit;
    this.spinBtn.classList.toggle('slot-spin--orbit', orbit);
    // Themed spin art (PNG frames) unless the brand opts out; the plain flat button otherwise.
    const art = theme.assets.spinButton;
    const artOn = !!art && (brand?.buttons.spinArt ?? true);
    this.spinBtn.classList.toggle('slot-spin--art', artOn);
    this.root.classList.toggle('slot-root--spin-art', artOn);
    for (const state of SPIN_ART_STATES) {
      if (artOn && art) this.spinArt[state].src = art[state];
      else this.spinArt[state].removeAttribute('src');
    }
    this.onSnapshot(this.snapshot);

    // logo
    this.brandLogo?.remove();
    this.brandLogo = null;
    if (brand?.logo) {
      const L = brand.logo;
      this.root.style.setProperty('--slot-brand-logo-height', `${L.height}px`);
      const img = el('img', `slot-brand-logo slot-brand-logo--${L.position}`);
      img.src = L.src;
      img.alt = L.alt ?? brand.name;
      let node: HTMLElement = img;
      if (L.href) {
        const a = el('a', 'slot-brand-logo-link');
        a.href = L.href;
        a.target = '_blank';
        a.rel = 'noopener';
        a.appendChild(img);
        node = a;
      }
      if (L.position === 'hud-left') this.readoutsRow.prepend(node);
      else if (L.position === 'hud-center' || L.position === 'hud-right') this.readoutsRow.appendChild(node);
      else this.root.appendChild(node);
      if (L.position === 'hud-center') node.classList.add('slot-brand-logo--hud-center');
      if (L.position === 'hud-right') node.classList.add('slot-brand-logo--hud-right');
      this.brandLogo = node;
    }
    // loading screen logo: brand logo wins over theme logo
    const loadingLogo = brand?.logo?.src ?? theme.assets.logo;
    if (loadingLogo) {
      this.loadingLogo.src = loadingLogo;
      this.loadingLogo.hidden = false;
    } else this.loadingLogo.hidden = true;
    // The loading screen names the game (operator title wins), not the chrome preset.
    const title = this.loading.querySelector('.slot-loading__title');
    if (title) title.textContent = theme.customTitle ?? theme.name;

    // custom CSS
    this.brandStyle?.remove();
    this.brandStyle = null;
    if (brand?.css) {
      const style = document.createElement('style');
      style.setAttribute('data-simple-slot-brand', brand.id);
      this.brandStyle = style;
      const url = brand.css;
      void fetch(url)
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
        .then((css) => {
          if (this.brandStyle === style) {
            style.textContent = css;
            document.head.appendChild(style);
          }
        })
        .catch(() => console.warn(`[simple-slot] brand css not loaded: ${url}`));
    }
    this.layout();
  }

  private layout(): void {
    const landscape = this.root.clientWidth > this.root.clientHeight;
    this.bar.classList.toggle('slot-hud--landscape', landscape);
    // Portrait: icons live in the readouts row so the control row fits [bet stepper] [SPIN].
    const wantParent = landscape ? this.controlsRow : this.readoutsRow;
    if (this.iconsGroup.parentElement !== wantParent) {
      if (landscape) this.controlsRow.prepend(this.iconsGroup);
      else this.readoutsRow.appendChild(this.iconsGroup);
    }
    const h = this.bar.offsetHeight || (landscape ? 96 : 150);
    const floating = this.brand?.hud.style === 'floating';
    const reserve = Math.round(h + (floating ? 12 : 0));
    const top = this.brand?.hud.position === 'top';
    const next = { '--slot-hud-top': top ? `${reserve}px` : '0px', '--slot-hud-bottom': top ? '0px' : `${reserve}px`, '--slot-hud-height': top ? '0px' : `${reserve}px` };
    let changed = false;
    for (const [k, v] of Object.entries(next)) {
      if (this.root.style.getPropertyValue(k) !== v) {
        this.root.style.setProperty(k, v);
        changed = true;
      }
    }
    if (changed) this.root.dispatchEvent(new CustomEvent('slot:hudresize'));
  }

  // ------------------------------------------------------------------ state

  private onSnapshot(s: GameSnapshot): void {
    this.snapshot = s;
    const S = this.strings;
    const spinning = s.state === 'spinning';
    const presenting = s.state === 'presenting' || s.state === 'freeSpinsIntro' || s.state === 'freeSpinsOutro';
    this.spinBtn.classList.toggle('is-stop', spinning && s.quickStopAvailable);
    this.spinBtn.classList.toggle('is-skip', presenting);
    this.spinBtn.classList.toggle('is-spinning', spinning);
    const enabled = (s.state === 'idle' && s.canSpin) || (spinning && s.quickStopAvailable) || presenting;
    // Busy = the round runs and the button cannot act on it (no quick stop): art shows the disabled frame + "SPINNING…".
    const busy = spinning && !s.quickStopAvailable && this.spinBtn.classList.contains('slot-spin--art');
    this.spinBtn.setAttribute('aria-busy', String(busy));
    this.setSpinLabel(spinning && s.quickStopAvailable ? t(S, 'stop') : presenting ? t(S, 'skip') : busy ? t(S, 'spinning') : t(S, 'spin'));
    this.spinBtn.disabled = !enabled;
    if (!enabled) this.spinBtn.classList.remove('is-pressed');
    const bets = this.o.game.getAvailableBets();
    const idx = bets.indexOf(s.bet);
    this.betMinus.disabled = !s.canChangeBet || idx <= 0;
    this.betPlus.disabled = !s.canChangeBet || idx >= bets.length - 1;
    this.betMax.disabled = !s.canChangeBet || idx >= bets.length - 1;
    if (s.mode === 'free' && s.freeSpins) {
      this.freeEl.textContent = `${s.freeSpins.remaining} / ${s.freeSpins.total}`;
      this.freeReadout.hidden = false;
    } else this.freeReadout.hidden = true;
    // minSpinMs may unlock the button without an event: poll briefly.
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (s.state === 'idle' && !s.canSpin && s.balance >= s.bet) this.retryTimer = setTimeout(() => this.onSnapshot(this.o.game.snapshot), 250);
    this.tickClock();
  }

  private setSpinLabel(text: string): void {
    if (this.spinLabel.textContent !== text) this.spinLabel.textContent = text;
  }

  private setWin(amount: number): void {
    this.winEl.textContent = formatMoney(amount, this.o.currency);
    this.winReadout.classList.toggle('is-zero', amount <= 0);
  }

  private tickClock(): void {
    const s = this.snapshot;
    if (!this.sessionReadout.hidden) this.sessionEl.textContent = formatDuration(Date.now() - s.sessionStartedAt);
    if (!this.netReadout.hidden) {
      const net = Math.round((s.sessionWon - s.sessionWagered) * 100) / 100;
      this.netEl.textContent = formatMoney(net, this.o.currency);
      this.netReadout.classList.toggle('is-negative', net < 0);
      this.netReadout.classList.toggle('is-positive', net > 0);
    }
  }

  private stepBet(dir: 1 | -1): void {
    const bets = this.o.game.getAvailableBets();
    const idx = bets.indexOf(this.snapshot.bet);
    const next = bets[Math.min(bets.length - 1, Math.max(0, idx + dir))];
    this.o.audio.play('click');
    this.o.game.setBet(next);
  }

  private maxBet(): void {
    const bets = this.o.game.getAvailableBets();
    this.o.audio.play('click');
    this.o.game.setBet(bets[bets.length - 1]);
  }

  private toggleSound(): void {
    const on = !this.o.audio.enabled;
    this.o.audio.setEnabled(on);
    this.renderIcon(this.soundBtn, on ? 'sound' : 'mute');
    this.soundBtn.classList.toggle('is-off', !on);
    if (on) this.o.audio.play('click');
  }

  private openPaytable(): void {
    if (this.paytable.isOpen) return this.paytable.close();
    this.settings.close();
    this.paytable.open(this.snapshot.bet);
  }

  private openSettings(): void {
    if (this.settings.isOpen) return this.settings.close();
    this.paytable.close();
    this.settings.open();
  }

  private handleKey(e: KeyboardEvent): void {
    if (e.repeat) return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
    if (e.code === 'Space' || e.code === 'Enter') {
      e.preventDefault();
      if (this.paytable.isOpen || this.settings.isOpen || this.realityModal?.open) return;
      if (!this.spinBtn.disabled) this.spinBtn.classList.add('is-pressed');
      this.o.game.press();
    } else if (e.code === 'Escape') {
      this.paytable.close();
      this.settings.close();
    }
  }

  private showToast(message: string): void {
    this.toast.textContent = message;
    this.toast.classList.add('is-visible');
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), 2200);
  }

  private showPaused(): void {
    if (this.realityModal?.open || this.pausedModal?.open) return;
    this.pausedModal = createModal(this.root, { title: t(this.strings, 'paused'), size: 'narrow', dismissible: false });
    this.pausedModal.body.appendChild(el('div', 'slot-paused-label', t(this.strings, 'paused')));
  }

  private hidePaused(): void {
    this.pausedModal?.close();
    this.pausedModal = null;
  }

  private checkReality(): void {
    const minutes = this.o.jurisdiction.realityCheckMinutes;
    if (!minutes || this.realityModal?.open) return;
    if (Date.now() - this.lastRealityCheck < minutes * 60_000) return;
    this.lastRealityCheck = Date.now();
    const elapsed = Math.round((Date.now() - this.snapshot.sessionStartedAt) / 60_000);
    this.o.game.pause();
    this.hidePaused();
    this.realityModal = createModal(this.root, { title: t(this.strings, 'sessionTime'), size: 'narrow', dismissible: false });
    this.realityModal.body.appendChild(el('p', undefined, t(this.strings, 'realityCheck', { m: elapsed })));
    const stats = el('dl', 'slot-stats');
    stats.append(
      el('dt', undefined, t(this.strings, 'bet')),
      el('dd', undefined, formatMoney(this.snapshot.sessionWagered, this.o.currency)),
      el('dt', undefined, t(this.strings, 'win')),
      el('dd', undefined, formatMoney(this.snapshot.sessionWon, this.o.currency)),
    );
    this.realityModal.body.appendChild(stats);
    const ok = el('button', 'slot-btn slot-btn--primary', t(this.strings, 'ok'));
    ok.type = 'button';
    ok.addEventListener('click', () => {
      this.realityModal?.close();
      this.realityModal = null;
      this.o.game.resume();
    });
    this.realityModal.actions.hidden = false;
    this.realityModal.actions.appendChild(ok);
  }

  // ------------------------------------------------------------------ public

  /** New theme (already branded by the caller). */
  setTheme(theme: ResolvedTheme): void {
    this.applyTheme(theme);
    this.paytable.setTheme(theme);
    this.applyBrand(this.brand, theme);
  }

  /** New brand (or null to remove branding); `theme` must be the theme with this brand applied. */
  setBrand(brand: ResolvedBrand | null, theme: ResolvedTheme): void {
    this.applyTheme(theme);
    this.paytable.setTheme(theme);
    this.applyBrand(brand, theme);
  }

  setStrings(s: Strings): void {
    this.strings = s;
    this.paytable.setStrings(s);
    this.settings.setStrings(s);
    this.betMax.textContent = t(s, 'maxBet');
    this.onSnapshot(this.snapshot);
  }

  showLoading(progress: number | null): void {
    if (progress == null) {
      this.loading.classList.add('is-hidden');
      return;
    }
    this.loading.classList.remove('is-hidden');
    this.loadingFill.classList.toggle('is-indeterminate', progress <= 0);
    this.loadingFill.style.width = `${Math.round(Math.min(1, Math.max(0, progress)) * 100)}%`;
    this.loadingLabel.textContent = `${t(this.strings, 'loading')} ${Math.round(progress * 100)}%`;
  }

  destroy(): void {
    for (const u of this.unsubscribe) u();
    for (const tmr of this.timers) clearInterval(tmr);
    if (this.toastTimer) clearTimeout(this.toastTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.resizeObserver?.disconnect();
    this.paytable.close();
    this.settings.close();
    this.pausedModal?.close();
    this.realityModal?.close();
    this.debugPanel?.destroy();
    this.brandLogo?.remove();
    this.brandStyle?.remove();
    this.bar.remove();
    this.toast.remove();
    this.loading.remove();
  }
}
